import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Divider,
} from "@material-ui/core";
import SeccionNarrativa from "./SeccionNarrativa";
import { formatearMonedaCliente } from "src/utils/formatearMonedaCliente";
import { formatValorItem } from "src/utils/propuestasV2/formatValorItem";
import { BRAND } from "src/theme/brandTokens";

const subtotalPorMoneda = (items) =>
  items.reduce((acc, it) => {
    const moneda = formatearMonedaCliente(it.tipoMoneda) || "CLP";
    const v = Number(it.valorConDescuento ?? 0);
    acc[moneda] = (acc[moneda] || 0) + v;
    return acc;
  }, {});

const formatSubtotal = (subtotalObj) =>
  Object.entries(subtotalObj)
    .map(([moneda, total]) => formatValorItem(moneda, total))
    .join(" + ");

const HeaderEmpresa = ({ nombre, rut, esRutProvisorio, subtotalTexto }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    justifyContent="space-between"
    alignItems={{ xs: "flex-start", sm: "center" }}
    sx={{ px: 2, py: 1.5, bgcolor: "grey.50" }}
    spacing={1}
  >
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {nombre || rut || "Empresa sin nombre"}
      </Typography>
      {rut && (
        <Typography variant="caption" color="textSecondary">
          · {rut}
        </Typography>
      )}
      {esRutProvisorio === 1 && (
        <Chip
          label="RUT provisorio"
          size="small"
          sx={{ bgcolor: BRAND.color.primary, color: "#fff", height: 20 }}
        />
      )}
    </Stack>
    {subtotalTexto && (
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, color: BRAND.color.primary }}
      >
        Subtotal: {subtotalTexto}
      </Typography>
    )}
  </Stack>
);

HeaderEmpresa.propTypes = {
  nombre: PropTypes.string,
  rut: PropTypes.string,
  esRutProvisorio: PropTypes.number,
  subtotalTexto: PropTypes.string,
};

const FilaItem = ({ item, numeroFase }) => (
  <TableRow hover>
    <TableCell sx={{ width: 52 }}>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ fontWeight: 600 }}
      >
        Fase {numeroFase}
      </Typography>
    </TableCell>
    <TableCell>
      <Chip
        label={item.tipoItem}
        size="small"
        sx={{
          bgcolor: item.tipoItem === "PLAN" ? "#E3F2FD" : "#FFF3E0",
          color: item.tipoItem === "PLAN" ? "#1565C0" : BRAND.color.primary,
          fontWeight: 600,
        }}
      />
    </TableCell>
    <TableCell>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {item.faseTitulo || item.nombreServicio || "—"}
      </Typography>
      {item.refTipoPlan && (
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ display: "block" }}
        >
          Plan: {item.refTipoPlan}
        </Typography>
      )}
      {item.refTipoTarea && (
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ display: "block" }}
        >
          Tarea: {item.refTipoTarea}
        </Typography>
      )}
    </TableCell>
    <TableCell>
      {item.empleado
        ? `${item.empleado.nombre ?? ""} ${item.empleado.apellidos ?? ""}`.trim()
        : item.refEmpleado || "—"}
    </TableCell>
    <TableCell align="right">
      {formatValorItem(item.tipoMoneda, item.valorOriginal)}
    </TableCell>
    <TableCell align="right">
      {item.descuento > 0 ? `${item.descuento}%` : "—"}
    </TableCell>
    <TableCell align="right" sx={{ fontWeight: 700 }}>
      {formatValorItem(item.tipoMoneda, item.valorConDescuento)}
    </TableCell>
  </TableRow>
);

FilaItem.propTypes = {
  item: PropTypes.object.isRequired,
  numeroFase: PropTypes.number.isRequired,
};

const TablaItems = ({ items, offsetNumero }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell />
        <TableCell>Tipo</TableCell>
        <TableCell>Fase / Servicio</TableCell>
        <TableCell>Responsable</TableCell>
        <TableCell align="right">Valor</TableCell>
        <TableCell align="right">Dto.</TableCell>
        <TableCell align="right">Total</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {items.map((item, idx) => (
        <FilaItem
          key={item.id ?? idx}
          item={item}
          numeroFase={offsetNumero + idx + 1}
        />
      ))}
    </TableBody>
  </Table>
);

TablaItems.propTypes = {
  items: PropTypes.array.isRequired,
  offsetNumero: PropTypes.number.isRequired,
};

/**
 * Sección "Metodología de Trabajo" del admin. Espejo del PDF:
 * - 1 empresa: header contextual con el cliente + tabla sin columna empresa.
 * - ≥2 empresas: grupos por empresa con subtotal propio + total general al final.
 *
 * Numeración de fase es continua entre grupos (coherente con la numeración del PDF).
 */
const ItemsPorEmpresa = ({ items }) => {
  const gruposEmpresas = useMemo(() => {
    const mapa = new Map();
    for (const item of items) {
      const key = item.refCliente || "__sin_empresa__";
      if (!mapa.has(key)) {
        mapa.set(key, {
          key,
          nombre: item.cliente?.nombre || null,
          rut: item.refCliente || null,
          esRutProvisorio: item.cliente?.esRutProvisorio ?? 0,
          items: [],
        });
      }
      mapa.get(key).items.push(item);
    }
    return Array.from(mapa.values());
  }, [items]);

  const multiEmpresa = gruposEmpresas.length > 1;
  const totalGeneralTexto = formatSubtotal(subtotalPorMoneda(items));

  if (items.length === 0) {
    return (
      <SeccionNarrativa titulo="Metodología de Trabajo">
        <Typography variant="body2" color="textSecondary">
          Esta propuesta aún no tiene fases / servicios registrados.
        </Typography>
      </SeccionNarrativa>
    );
  }

  return (
    <SeccionNarrativa titulo="Metodología de Trabajo">
      {!multiEmpresa && gruposEmpresas[0] && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="textSecondary">
            Propuesta para
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {gruposEmpresas[0].nombre || gruposEmpresas[0].rut || "—"}
            </Typography>
            {gruposEmpresas[0].rut && (
              <Typography variant="caption" color="textSecondary">
                · {gruposEmpresas[0].rut}
              </Typography>
            )}
            {gruposEmpresas[0].esRutProvisorio === 1 && (
              <Chip
                label="RUT provisorio"
                size="small"
                sx={{ bgcolor: BRAND.color.primary, color: "#fff", height: 20 }}
              />
            )}
          </Stack>
        </Box>
      )}

      {multiEmpresa ? (
        <Stack spacing={2}>
          {gruposEmpresas.map((grupo, idx) => {
            const offsetNumero = gruposEmpresas
              .slice(0, idx)
              .reduce((acc, g) => acc + g.items.length, 0);
            const subtotalTexto = formatSubtotal(
              subtotalPorMoneda(grupo.items),
            );
            return (
              <Paper
                key={grupo.key}
                variant="outlined"
                sx={{ overflow: "hidden", borderRadius: 2 }}
              >
                <HeaderEmpresa
                  nombre={grupo.nombre}
                  rut={grupo.rut}
                  esRutProvisorio={grupo.esRutProvisorio}
                  subtotalTexto={subtotalTexto}
                />
                <Divider />
                <TablaItems items={grupo.items} offsetNumero={offsetNumero} />
              </Paper>
            );
          })}

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              bgcolor: "#FFF3E0",
              borderColor: BRAND.color.primary,
              borderRadius: 2,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Total general
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: BRAND.color.primary }}
              >
                {totalGeneralTexto}
              </Typography>
            </Stack>
          </Paper>
        </Stack>
      ) : (
        <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 2 }}>
          <TablaItems items={gruposEmpresas[0].items} offsetNumero={0} />
        </Paper>
      )}
    </SeccionNarrativa>
  );
};

ItemsPorEmpresa.propTypes = {
  items: PropTypes.array.isRequired,
};

export default ItemsPorEmpresa;
